// Dates du calendrier. Un jour est une chaîne « AAAA-MM-JJ » (DayKey) : l'arithmétique se fait
// en UTC, ce qui évite les surprises des changements d'heure. Les instants sont convertis dans
// le fuseau de l'utilisateur, qui peut différer de celui du navigateur.

export type DayKey = string;

const MS_DAY = 86_400_000;
const asUtc = (key: DayKey) => new Date(`${key}T00:00:00Z`);
const toKey = (d: Date): DayKey => d.toISOString().slice(0, 10);

export function addDays(key: DayKey, n: number): DayKey {
  return toKey(new Date(asUtc(key).getTime() + n * MS_DAY));
}

export const daysBetween = (a: DayKey, b: DayKey) => Math.round((asUtc(b).getTime() - asUtc(a).getTime()) / MS_DAY);

/** 0 = dimanche … 6 = samedi */
export const weekday = (key: DayKey) => asUtc(key).getUTCDay();

export const startOfWeek = (key: DayKey, weekStart: number) => addDays(key, -((weekday(key) - weekStart + 7) % 7));

export const startOfMonth = (key: DayKey) => `${key.slice(0, 8)}01`;

export function addMonths(key: DayKey, n: number): DayKey {
  const d = asUtc(startOfMonth(key));
  d.setUTCMonth(d.getUTCMonth() + n);
  return toKey(d);
}

export const isWeekend = (key: DayKey) => weekday(key) === 0 || weekday(key) === 6;

/** Numéro de semaine ISO 8601. */
export function isoWeek(key: DayKey): number {
  // Le jeudi de la semaine détermine l'année ; la semaine 1 contient le 4 janvier.
  const thursday = asUtc(addDays(key, 3 - ((weekday(key) + 6) % 7)));
  const jan4 = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const week1Monday = jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * MS_DAY;
  return 1 + Math.floor((thursday.getTime() - week1Monday) / (7 * MS_DAY));
}

export const isDayKey = (s: unknown): s is DayKey => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(asUtc(s).getTime());

// ───── Fuseaux horaires ─────

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string) {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(tz, f);
  }
  return f;
}

function zonedParts(ms: number, tz: string) {
  const p: Record<string, number> = {};
  for (const { type, value } of partsFormatter(tz).formatToParts(new Date(ms))) if (type !== "literal") p[type] = Number(value);
  return p as { year: number; month: number; day: number; hour: number; minute: number; second: number };
}

/** Jour local d'un instant dans le fuseau donné. */
export function dayKeyIn(date: Date | string | number, tz: string): DayKey {
  const p = zonedParts(new Date(date).getTime(), tz);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Minutes écoulées depuis minuit local. */
export function minutesIn(date: Date | string | number, tz: string): number {
  const p = zonedParts(new Date(date).getTime(), tz);
  return p.hour * 60 + p.minute;
}

function offsetMs(ms: number, tz: string) {
  const p = zonedParts(ms, tz);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
}

/** Instant correspondant à un jour et une heure locale (minutes depuis minuit) dans le fuseau donné. */
export function zonedInstant(key: DayKey, minutes: number, tz: string): Date {
  const guess = asUtc(key).getTime() + minutes * 60_000;
  const first = guess - offsetMs(guess, tz);
  // Second passage pour les jours de changement d'heure.
  return new Date(guess - offsetMs(first, tz));
}

export const todayIn = (tz: string) => dayKeyIn(Date.now(), tz);

/** Date « neutre » pour Intl : midi UTC du jour, formatée en UTC. */
export const noonUtc = (key: DayKey) => new Date(`${key}T12:00:00Z`);
